import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { Copy, Download, Search, Terminal, X } from 'lucide-react';

import { useProductPolling } from '@codes/product';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input } from '@codes/ui-kit';

export const ServiceEventLogDialog = ({
    productId,
    open,
    setOpen,
    nested = false,
    preventDismiss = true,
}: {
    productId: string;
    open: boolean;
    setOpen: (open: boolean) => void;
    nested?: boolean;
    preventDismiss?: boolean;
}) => {
    const { data: product, isFetching, isRefetching, error, refetch } = useProductPolling(productId);

    const { t } = useTranslation();

    const logContainerRef = useRef<HTMLDivElement>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const eventLogs = useMemo(() => product?.logEvents ?? [], [product]);

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(eventLogs.join('\n'));
            toast({ title: 'Success to copied' });
        } catch (err) {
            console.error('Failed to copy logs: ', err);
            toast({ title: 'Failed to copy logs' });
        }
    };

    const downloadLogs = () => {
        const blob = new Blob([eventLogs.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `event-logs-${productId}-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const formatLogLine = (log: string, index: number) => {
        const logLower = log.toLowerCase();
        const trimmedLog = log.trim();

        // JSON 블록 감지 (확실한 JSON 라인들)
        const isJsonBrace = /^[{}]$/.test(trimmedLog);
        const isJsonProperty = /^"[^"]+":/.test(trimmedLog);

        // Enhanced error detection with comprehensive patterns
        const isError =
            /\b(error|err|fail|failed|failure|exception|fatal|critical|denied|refused|invalid|cannot|unable)\b/i.test(
                log
            ) ||
            log.includes('❌') ||
            log.includes('✗') ||
            log.includes('ERROR') ||
            /^\[ERROR\]/i.test(trimmedLog) ||
            /^ERROR:/i.test(trimmedLog);
        const isWarning = /\b(warn|warning|deprecated|invalid|note:)\b/i.test(log) || log.includes('⚠️');
        const isSuccess =
            /\b(success|complete|deployed|verified|done)\b/i.test(log) || log.includes('✅') || log.includes('✔');

        // Basic patterns for different log types
        const isStep = /^STEP\s+\d+:/i.test(log);
        const isCommand =
            /^(\$|>|\w+\s+\w+)/i.test(log.trim()) &&
            (logLower.includes('npm') ||
                logLower.includes('yarn') ||
                logLower.includes('sls') ||
                logLower.includes('node'));
        const isUrl = /https?:\/\//.test(log);
        const hasTimestamp = /\d{4}-\d{2}-\d{2}|\d{2}:\d{2}:\d{2}/.test(log);

        // Determine styling based on priority order
        let lineClass = 'text-slate-300';
        let prefix = '';
        let bgClass = 'hover:bg-slate-800/50';

        if (isJsonBrace || isJsonProperty) {
            lineClass = 'text-cyan-400 dark:text-cyan-300 font-mono';
            prefix = '';
            bgClass = 'hover:bg-cyan-950/20';
        } else if (isError) {
            lineClass = 'text-red-400 dark:text-red-300';
            prefix = '❌ ';
            bgClass = 'hover:bg-red-950/20';
        } else if (isWarning) {
            lineClass = 'text-yellow-500 dark:text-yellow-400';
            prefix = '⚠️  ';
            bgClass = 'hover:bg-yellow-950/20';
        } else if (isSuccess) {
            lineClass = 'text-green-500 dark:text-green-400';
            prefix = '✅ ';
            bgClass = 'hover:bg-green-950/20';
        } else if (isStep) {
            lineClass = 'text-cyan-400 dark:text-cyan-300 font-semibold';
            prefix = '🚀 ';
            bgClass = 'hover:bg-cyan-950/20';
        } else if (isCommand) {
            lineClass = 'text-purple-400 dark:text-purple-300 font-mono';
            prefix = '$ ';
            bgClass = 'hover:bg-purple-950/20';
        } else if (isUrl) {
            lineClass = 'text-blue-400 dark:text-blue-300';
            prefix = '🌐 ';
            bgClass = 'hover:bg-blue-950/20';
        } else if (hasTimestamp) {
            lineClass = 'text-indigo-400 dark:text-indigo-300';
            prefix = 'ℹ️  ';
            bgClass = 'hover:bg-indigo-950/20';
        }

        return { lineClass, prefix, isCommand, bgClass, isJsonProperty, isError, isWarning, isSuccess };
    };

    const filteredLogs = useMemo(() => {
        if (!searchQuery) {
            return eventLogs;
        }

        return eventLogs.filter(log => {
            return log.toLowerCase().includes(searchQuery.toLowerCase());
        });
    }, [eventLogs, searchQuery]);

    const InnerView = useMemo(() => {
        if ((isFetching || isRefetching) && !product) {
            return (
                <div className="bg-slate-950 dark:bg-slate-900 border border-slate-800 dark:border-slate-700 rounded-lg m-4">
                    <div className="flex items-center justify-between bg-slate-900 dark:bg-slate-800 px-4 py-2 rounded-t-lg border-b border-slate-700 dark:border-slate-600">
                        <span className="text-sm font-mono text-slate-300">Loading logs...</span>
                        <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-ping"></div>
                            <div
                                className="w-2 h-2 bg-green-400 rounded-full animate-ping"
                                style={{ animationDelay: '0.2s' }}
                            ></div>
                            <div
                                className="w-2 h-2 bg-green-400 rounded-full animate-ping"
                                style={{ animationDelay: '0.4s' }}
                            ></div>
                        </div>
                    </div>
                    <div className="bg-slate-950 dark:bg-slate-900 font-mono text-sm p-4 rounded-b-lg">
                        {/* Animated loading lines that look like terminal output */}
                        {Array.from({ length: 12 }).map((_, i) => (
                            <div
                                key={i}
                                className="flex items-start gap-2 py-0.5 animate-pulse"
                                style={{ animationDelay: `${i * 100}ms` }}
                            >
                                <span className="text-slate-600 text-xs mt-0.5 min-w-[2rem] text-right select-none">
                                    {(i + 1).toString().padStart(3, '0')}
                                </span>
                                <div className="flex-1">
                                    <div
                                        className="h-4 bg-slate-800 dark:bg-slate-700 rounded animate-pulse"
                                        style={{
                                            width: `${Math.random() * 40 + 30}%`,
                                            animationDelay: `${i * 150}ms`,
                                        }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="bg-slate-950 dark:bg-slate-900 border border-slate-800 dark:border-slate-700 rounded-lg m-4">
                    <div className="flex items-center justify-between bg-slate-900 dark:bg-slate-800 px-4 py-2 rounded-t-lg border-b border-slate-700 dark:border-slate-600">
                        <span className="text-sm font-mono text-slate-300">Error Loading Logs</span>
                    </div>
                    <div className="flex flex-col gap-y-3 min-h-64 items-center justify-center p-8">
                        <div className="text-red-400 font-mono text-sm">
                            <span role="img" aria-label="Error">
                                ❌
                            </span>{' '}
                            {t('serviceSetting.eventLogsLoadError')}
                        </div>
                        <Button
                            onClick={() => refetch()}
                            variant="outline"
                            className="bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Retry
                        </Button>
                    </div>
                </div>
            );
        }

        if (eventLogs.length === 0) {
            return (
                <div className="bg-slate-950 dark:bg-slate-900 border border-slate-800 dark:border-slate-700 rounded-lg m-4">
                    <div className="flex items-center justify-between bg-slate-900 dark:bg-slate-800 px-4 py-2 rounded-t-lg border-b border-slate-700 dark:border-slate-600">
                        <span className="text-sm font-mono text-slate-300">No Logs Available</span>
                    </div>
                    <div className="flex flex-col gap-y-3 min-h-64 items-center justify-center p-8">
                        <div className="text-yellow-400 font-mono text-sm">
                            <span role="img" aria-label="Warning">
                                ⚠️
                            </span>{' '}
                            {t('serviceSetting.eventLogsNotFound')}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-slate-950 dark:bg-slate-900 border border-slate-800 dark:border-slate-700 rounded-lg m-4">
                {/* Terminal Header */}
                <div className="flex items-center justify-center bg-slate-900 dark:bg-slate-800 px-4 py-3 rounded-t-lg border-b border-slate-700 dark:border-slate-600 gap-3">
                    {/* Search Input - Centered */}
                    <div className="flex-1 max-w-2xl relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            placeholder="Search logs..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="h-9 pl-10 pr-10 text-sm bg-slate-800 border-slate-600 text-slate-200 placeholder:text-slate-400 focus:border-slate-500"
                        />
                        {searchQuery && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSearchQuery('')}
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-slate-400 hover:text-slate-200 hover:bg-slate-700"
                            >
                                <X className="w-3.5 h-3.5" />
                            </Button>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 ml-auto">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={copyToClipboard}
                            className="h-9 px-3 text-slate-400 hover:text-slate-200 hover:bg-slate-700"
                            title="Copy logs"
                        >
                            <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={downloadLogs}
                            className="h-9 px-3 text-slate-400 hover:text-slate-200 hover:bg-slate-700"
                            title="Download logs"
                        >
                            <Download className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Terminal Content */}
                <div
                    ref={logContainerRef}
                    className="max-h-[60vh] overflow-auto bg-slate-950 dark:bg-slate-900 font-mono text-sm leading-relaxed p-4 rounded-b-lg"
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#475569 #1e293b',
                    }}
                >
                    <style jsx>{`
                        div::-webkit-scrollbar {
                            width: 8px;
                        }
                        div::-webkit-scrollbar-track {
                            background: #1e293b;
                        }
                        div::-webkit-scrollbar-thumb {
                            background: #475569;
                            border-radius: 4px;
                        }
                        div::-webkit-scrollbar-thumb:hover {
                            background: #64748b;
                        }
                    `}</style>

                    {filteredLogs.length === 0 ? (
                        <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
                            No logs match the current filter
                        </div>
                    ) : (
                        filteredLogs.map((log, idx) => {
                            const { lineClass, prefix, isCommand, bgClass, isJsonProperty } = formatLogLine(log, idx);
                            const originalIdx = eventLogs.indexOf(log);
                            return (
                                <div
                                    key={originalIdx}
                                    className={`flex items-start gap-2 py-1 px-2 -mx-2 rounded transition-colors duration-150 ${
                                        bgClass || 'hover:bg-slate-800/50'
                                    }`}
                                >
                                    <span className="text-slate-500 text-xs mt-0.5 min-w-[2rem] text-right select-none font-mono">
                                        {(originalIdx + 1).toString().padStart(3, '0')}
                                    </span>
                                    <div
                                        className={`flex-1 ${lineClass} whitespace-pre-wrap break-all leading-relaxed`}
                                    >
                                        {prefix && (
                                            <span className="mr-2 inline-block" role="img">
                                                {prefix}
                                            </span>
                                        )}
                                        <span
                                            className={isCommand ? 'select-all font-mono' : ''}
                                            style={isJsonProperty ? { paddingLeft: '1rem' } : undefined}
                                        >
                                            {log}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    }, [product, isFetching, isRefetching, error, refetch, eventLogs, filteredLogs, searchQuery]);

    useLayoutEffect(() => {
        const el = logContainerRef.current;
        if (!el) {
            return;
        }

        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [filteredLogs.length]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                className="p-0 max-w-[70vw] bg-background"
                aria-describedby={undefined}
                nested={nested}
                preventDismiss={preventDismiss}
            >
                <DialogHeader className="px-6 py-4">
                    <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                        <Terminal className="w-5 h-5" />
                        {t('serviceSetting.eventLogs')}
                    </DialogTitle>
                </DialogHeader>
                {InnerView}
            </DialogContent>
        </Dialog>
    );
};
